import { type FC, useCallback } from 'react'

import { usePostProfileInteractionsName } from '@dhl/receiver-profile-api'

import { isFeatureEnabled } from '~/config/config.js'
import { steps } from './model/steps.js'
import { SetupWizard } from './SetupWizard.js'
import { Branch, BranchPath, Step, StepGroup } from './view/components/Step.js'
import { AdditionalEmailAddresses } from './view/components/steps/AdditionalEmailAddresses.js'
import { DeliveryPreference } from './view/components/steps/DeliveryPreference.js'
import { DeliveryPreferenceAgreedPlace } from './view/components/steps/DeliveryPreferenceAgreedPlace.js'
import { DeliveryPreferenceChoice } from './view/components/steps/DeliveryPreferenceChoice.js'
import { DeliveryPreferenceIntro } from './view/components/steps/DeliveryPreferenceIntro.js'
import { DeliveryPreferenceServicePointList } from './view/components/steps/DeliveryPreferenceServicePointList.js'
import { DeliveryPreferenceServicePointSelect } from './view/components/steps/DeliveryPreferenceServicePointSelect.js'
import { DeliveryPreferenceToDoor } from './view/components/steps/DeliveryPreferenceToDoor.js'
import { Done } from './view/components/steps/Done.js'
import { NotificationPreference } from './view/components/steps/NotificationPreference.js'
import { PhoneNumber } from './view/components/steps/PhoneNumber.js'
import { PhoneNumberVerification } from './view/components/steps/PhoneNumberVerification.js'
import { Welcome } from './view/components/steps/Welcome.js'
import { YourAddress } from './view/components/steps/YourAddress.js'

type SetupPageComponentProps = {
  deliveryPreferencesV2Enabled: boolean
}

// The SetupPageComponent is separated from the SetupPage to allow for easier testing of the setup flow
// without needing to mock feature flags.
export const SetupPageComponent: FC<SetupPageComponentProps> = ({ deliveryPreferencesV2Enabled }) => {
  const { mutateAsync: postCompleteGuidedTour } = usePostProfileInteractionsName()

  const handleStepComplete = useCallback(
    async (name: string) => {
      await postCompleteGuidedTour({
        name,
      })
    },
    [postCompleteGuidedTour]
  )

  return (
    <SetupWizard onStepComplete={handleStepComplete}>
      <Step component={Welcome} name={steps.welcome} />
      <Step optional component={YourAddress} name={steps.address} />
      <StepGroup optional>
        <Step component={DeliveryPreferenceIntro} name={steps.deliveryPreferenceIntro} />

        {!deliveryPreferencesV2Enabled && <Step component={DeliveryPreference} name={steps.deliveryPreference} />}

        {deliveryPreferencesV2Enabled && (
          <>
            <Step component={DeliveryPreferenceChoice} name={steps.deliveryPreference} />

            <Branch optional stateKey="deliveryPreference">
              <BranchPath value="toDoor">
                <Step component={DeliveryPreferenceToDoor} name={steps.deliveryPreferenceToDoor} />
              </BranchPath>
              <BranchPath value="servicePoint">
                <Step component={DeliveryPreferenceServicePointList} name={steps.deliveryPreferenceServicePointList} />
                <Step
                  component={DeliveryPreferenceServicePointSelect}
                  name={steps.deliveryPreferenceServicePointSelect}
                />
              </BranchPath>
              <BranchPath value="agreedPlace">
                <Step component={DeliveryPreferenceAgreedPlace} name={steps.deliveryPreferenceAgreedPlace} />
              </BranchPath>
            </Branch>
          </>
        )}
      </StepGroup>
      <Step optional component={AdditionalEmailAddresses} name={steps.additionalEmailAddress} />
      <StepGroup disabled>
        <Step component={PhoneNumber} name={steps.phoneNumber} />
        <Step component={PhoneNumberVerification} name={steps.phoneNumberVerification} />
      </StepGroup>
      <Step optional component={NotificationPreference} name={steps.notificationPreference} />
      <Step component={Done} name={steps.done} />
    </SetupWizard>
  )
}

export const SetupPage: FC = () => (
  <SetupPageComponent
    deliveryPreferencesV2Enabled={isFeatureEnabled('profile.deliveryPreferences.deliveryPreferencesV2')}
  />
)
